import React from "react";
import {Box, Table, TableBody, TableCell, TableRow, Typography} from "@mui/material";

const PLAIN_VALUE_BOX_SX = {
    m: 0,
    p: 1.5,
    bgcolor: "grey.100",
    borderRadius: 1,
    fontFamily: "monospace",
    fontSize: "0.75rem",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    maxHeight: 300,
    overflow: "auto"
};

// Se il valore è già un oggetto/array lo si considera JSON. Se è una stringa che
// sembra contenere JSON, si prova a fare il parsing: se fallisce, si tratta come
// semplice stringa.
function parseAsJsonIfPossible(value) {
    if (value !== null && typeof value === "object") {
        return {isJson: true, data: value};
    }

    if (typeof value === "string") {
        const trimmed = value.trim();
        const looksLikeJson = (trimmed.startsWith("{") && trimmed.endsWith("}"))
            || (trimmed.startsWith("[") && trimmed.endsWith("]"));

        if (looksLikeJson) {
            try {
                return {isJson: true, data: JSON.parse(trimmed)};
            } catch (e) {
                // Non è JSON valido nonostante l'aspetto: la si tratta come stringa semplice.
            }
        }
    }

    return {isJson: false, data: value};
}

function JsonPropertyTable({data}) {
    const isArray = Array.isArray(data);
    const entries = isArray ? data.map((item, index) => [`[${index}]`, item]) : Object.entries(data);

    if (entries.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{fontStyle: "italic", p: 1}}>
                {isArray ? "Array vuoto" : "Oggetto vuoto"}
            </Typography>
        );
    }

    return (
        <Table size="small">
            <TableBody>
                {entries.map(([key, value]) => (
                    <TableRow key={key} sx={{verticalAlign: "top", "&:last-child td": {borderBottom: 0}}}>
                        <TableCell sx={{width: "35%", fontWeight: 600, whiteSpace: "nowrap"}}>
                            {key}
                        </TableCell>
                        <TableCell sx={{p: value !== null && typeof value === "object" ? 0 : 1}}>
                            <JsonValue value={value}/>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

function JsonValue({value}) {
    if (value === null || value === undefined) {
        return <Typography variant="body2" color="text.secondary">Non disponibile</Typography>;
    }

    if (typeof value === "object") {
        return (
            <Box sx={{borderLeft: "2px solid", borderColor: "divider", ml: 1}}>
                <JsonPropertyTable data={value}/>
            </Box>
        );
    }

    if (typeof value === "boolean") {
        return <Typography variant="body2">{value ? "Sì" : "No"}</Typography>;
    }

    return <Typography component="pre" variant="body2" sx={{wordBreak: "break-word"}}>{String(value)}</Typography>;
}

// Mostra un valore di debug (tipicamente una richiesta/risposta di un servizio esterno)
// in modo leggibile: se il contenuto è JSON (oggetto/array, anche annidato, o una stringa
// che lo rappresenta) viene reso come tabella proprietà/valore a cascata; altrimenti il
// valore viene mostrato come semplice testo in un box grigio.
export function JsonDataViewer({value, emptyLabel = "Non disponibile"}) {
    if (value === null || value === undefined || value === "") {
        return <Box component="pre" sx={PLAIN_VALUE_BOX_SX}>{emptyLabel}</Box>;
    }

    const {isJson, data} = parseAsJsonIfPossible(value);

    if (isJson && data !== null && typeof data === "object") {
        return (
            <Box sx={{border: "1px solid", borderColor: "divider", borderRadius: 1}}>
                <JsonPropertyTable data={data}/>
            </Box>
        );
    }

    return <Box component="pre" sx={PLAIN_VALUE_BOX_SX}>{String(data)}</Box>;
}
